import { Link } from "react-router-dom";
import { Bus, ShieldCheck, Users, Zap } from "lucide-react";

import type { ApplicationStatus } from "@/features/applications/types";
import { CATEGORY_LABELS, ROLE_LABELS, type Shift } from "@/features/shifts/types";
import { formatTimeRange12h } from "@/shared/lib/time";
import { Badge, type BadgeProps } from "@/shared/ui";

const SHIFT_STATUS: Record<string, { label: string; variant: BadgeProps["variant"] }> = {
  OPEN: { label: "Open", variant: "success" },
  FILLED: { label: "Filled", variant: "info" },
  COMPLETED: { label: "Completed", variant: "neutral" },
  CANCELLED: { label: "Cancelled", variant: "destructive" },
  NO_SHOW: { label: "No-show", variant: "warning" },
};

const APPLICATION_BADGE: Record<
  ApplicationStatus,
  { label: string; variant: BadgeProps["variant"] }
> = {
  PENDING: { label: "Applied", variant: "warning" },
  INTERESTED: { label: "Shortlisted", variant: "success" },
  PASSED: { label: "Passed", variant: "neutral" },
  CONFIRMED: { label: "Confirmed", variant: "success" },
  WITHDRAWN: { label: "Withdrawn", variant: "neutral" },
};

function extractCity(address: string): string | null {
  const parts = address.split(",").map((p) => p.trim());
  return parts.length >= 2 ? parts[1] : null;
}

function computeHours(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let minutes = eh * 60 + em - (sh * 60 + sm);
  if (minutes <= 0) minutes += 24 * 60;
  return Math.round((minutes / 60) * 10) / 10;
}

function relativeDay(dateStr: string): string | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateStr);
  date.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  return null;
}

function formatDateRange(start: string, end: string | null): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  if (!end || end === start) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

function daySpan(start: string, end: string | null): number {
  if (!end || end === start) return 1;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
}

interface Props {
  shift: Shift;
  to: string;
  showStatus?: boolean;
  myApplicationStatus?: ApplicationStatus;
  hideVenueSummary?: boolean;
}

export function ShiftCard({
  shift,
  to,
  showStatus = false,
  myApplicationStatus,
  hideVenueSummary = false,
}: Props) {
  const city = extractCity(shift.venue_address);
  const hoursPerDay = computeHours(shift.start_time, shift.end_time);
  const numDays = daySpan(shift.date, shift.end_date);
  const totalHours = +(hoursPerDay * numDays).toFixed(1);
  const totalPay = (parseFloat(shift.pay_rate) * totalHours).toFixed(0);
  const relative = relativeDay(shift.date);
  const statusMeta = SHIFT_STATUS[shift.status];
  const applicationMeta = myApplicationStatus ? APPLICATION_BADGE[myApplicationStatus] : null;

  return (
    <Link
      to={to}
      className="group relative block rounded-lg border border-border bg-surface p-5 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-border-strong hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="min-w-0 truncate text-base font-semibold tracking-display-tight text-copy" title={shift.event_name ?? ROLE_LABELS[shift.role]}>
              {shift.event_name ?? ROLE_LABELS[shift.role]}
            </h3>
            {shift.event_name && (
              <Badge variant="outline">{ROLE_LABELS[shift.role]}</Badge>
            )}
            {shift.is_demo && (
              <Badge
                variant="warning"
                title="Seeded by scripts/seed_demo.py — not a real venue posting"
              >
                Demo
              </Badge>
            )}
            {shift.is_urgent && (
              <Badge variant="destructive">
                <Zap className="h-3 w-3" />
                Urgent
                {shift.urgency_premium_percent != null &&
                  ` +${shift.urgency_premium_percent}%`}
              </Badge>
            )}
            {numDays > 1 && (
              <Badge variant="info">
                {numDays}-day
              </Badge>
            )}
            {shift.transportation_offered && (
              <Badge variant="neutral">
                <Bus className="h-3 w-3" />
                Transit
              </Badge>
            )}
            {shift.category && (
              <Badge variant="outline">
                {CATEGORY_LABELS[shift.category]}
              </Badge>
            )}
            {applicationMeta && (
              <Badge variant={applicationMeta.variant} dot>
                {applicationMeta.label}
              </Badge>
            )}
            {showStatus && statusMeta && (
              <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
            )}
          </div>

          {!hideVenueSummary && (
            <p className="mt-0.5 truncate text-sm text-copy-muted">
              <span className="font-medium text-copy/85">{shift.venue_name}</span>
              {city && <span className="text-copy-subtle"> · {city}</span>}
              {shift.venue_rating_avg != null && (
                <span className="text-copy-subtle">
                  {" · "}
                  <span className="text-warning">★</span> {shift.venue_rating_avg.toFixed(1)}
                </span>
              )}
            </p>
          )}

          <p className="mt-2 text-xs text-copy-muted">
            {formatDateRange(shift.date, shift.end_date)}
            {relative && <span className="font-medium text-copy"> · {relative}</span>}
            {" · "}
            {formatTimeRange12h(shift.start_time, shift.end_time)}{" "}
            <span className="text-copy-subtle">
              ({hoursPerDay}h{numDays > 1 ? `/day · ${totalHours}h total` : ""})
            </span>
          </p>

          {shift.posting_slots_total > 1 && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-copy-muted">
              <Users className="h-3.5 w-3.5 text-warning" />
              <span className="tabular-nums">
                {shift.posting_slots_filled}/{shift.posting_slots_total}
              </span>{" "}
              spots filled
            </p>
          )}
        </div>

        <div className="shrink-0 text-right">
          <p className="text-lg font-semibold tracking-display-tight tabular-nums text-copy">
            ${shift.pay_rate}
            <span className="text-sm font-normal text-copy-muted">/hr</span>
          </p>
          <p className="text-xs tabular-nums text-copy-muted">~${totalPay} total</p>
        </div>
      </div>

      {shift.required_certifications && shift.required_certifications.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs text-copy-muted">
            <ShieldCheck className="h-3.5 w-3.5" />
            Requires:
          </span>
          {shift.required_certifications.map((cert) => (
            <Badge key={cert} variant="neutral">
              {cert}
            </Badge>
          ))}
        </div>
      )}

      {shift.pending_applicant_count != null && shift.pending_applicant_count > 0 && (
        <div className="mt-3">
          <Badge variant="success" dot>
            {shift.pending_applicant_count} new applicant
            {shift.pending_applicant_count !== 1 ? "s" : ""}
          </Badge>
        </div>
      )}

      {shift.notes && (
        <p className="mt-3 line-clamp-2 border-t border-border pt-3 text-sm text-copy-muted">
          {shift.notes}
        </p>
      )}
    </Link>
  );
}
